import ENV from "config/Env";
import { typedQueryClient, useTypedMutation } from "hooks";
import { DealersDashboardQueryKeys } from "modules/DealersDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { DealerID, DealerLocationID } from "models";

type LinkExactAccountPayload = {
  account_id: string;
  dealer_id?: DealerID;
  dealer_location_id?: DealerLocationID;
};

const setExactAccountIdInList = ({ account_id, dealer_id, dealer_location_id }: LinkExactAccountPayload) => {
  const dealers = typedQueryClient.getQueryData(DealersDashboardQueryKeys.list);
  if (!dealers) return;

  const dealerIdx = dealers.findIndex(dealer => dealer.id === dealer_id || dealer.locations?.some(location => location.id === dealer_location_id));
  if (dealerIdx === -1) return;

  const dealer = dealers[dealerIdx];
  const locations = dealer.locations ?? [];
  const locationIdx = dealer_location_id ? locations.findIndex(location => location.id === dealer_location_id) : -1;

  typedQueryClient.setQueryData(
    DealersDashboardQueryKeys.list,
    dealers.with(dealerIdx, {
      ...dealer,
      ...(dealer.id === dealer_id && { exact_account_id: account_id }),
      ...(locationIdx !== -1 && { locations: locations.with(locationIdx, { ...locations[locationIdx], exact_account_id: account_id }) })
    })
  );
};

export const setExactAccountIdInCache = ({ account_id, dealer_id, dealer_location_id }: LinkExactAccountPayload) => {
  if (dealer_id) {
    const dealer = typedQueryClient.getQueryData(DealersDashboardQueryKeys.dealerDetails(dealer_id));
    if (dealer) typedQueryClient.setQueryData(DealersDashboardQueryKeys.dealerDetails(dealer_id), { ...dealer, exact_account_id: account_id });
  }

  if (dealer_location_id) {
    const location = typedQueryClient.getQueryData(DealersDashboardQueryKeys.locationDetails(dealer_location_id));
    if (location) typedQueryClient.setQueryData(DealersDashboardQueryKeys.locationDetails(dealer_location_id), { ...location, exact_account_id: account_id });
  }

  setExactAccountIdInList({ account_id, dealer_id, dealer_location_id });
};

export const useLinkExactAccountMutation = () =>
  useTypedMutation<LinkExactAccountPayload, void>({
    mutationFn: async payload => (await ApiInstance.post("/accounts/link", payload, ENV.exactBaseURL)).data,
    onSuccess: setExactAccountIdInCache
  });
